import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { authApi, type AuthUser } from "../api/client";

/**
 * Auth state hook. The session is server-side (HttpOnly cookie) so
 * we ask /api/auth/me on mount and react to its result.
 *
 * - data === AuthUser  → logged in
 * - data === null      → confirmed logged out
 * - isLoading          → first check still pending
 */
export function useAuth() {
  return useQuery<AuthUser | null>({
    queryKey: ["auth", "me"],
    queryFn: async () => {
      try {
        return await authApi.me();
      } catch (e) {
        // request() already redirects on 401, but useAuth runs on the
        // login page too — in that case swallow and return null.
        return null;
      }
    },
    retry: false,
    staleTime: 60_000,
  });
}

export function useLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ username, password }: { username: string; password: string }) =>
      authApi.login(username, password),
    onSuccess: (user) => {
      qc.setQueryData(["auth", "me"], user);
    },
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => authApi.logout(),
    onSuccess: () => {
      qc.setQueryData(["auth", "me"], null);
      qc.clear();
      window.location.replace("/login");
    },
  });
}
